import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';

import { AuthTags } from '~constants/authTags';

import { getErrorMessage } from '~libs/axiosWrapper';
import dayjs, { dateInputFieldFormat } from '~libs/dayjs';
import { useSearch } from '~libs/searchParams';
import {
  buildFilterParams,
  EMPTY_FILTERS,
  ContributionFilters as FilterValues,
  hasActiveFilters,
} from '~support-at-home/lib/contributionFilters';
import {
  buildNewContributionRow,
  flattenContributionGroups,
  insertAfterClient,
  isClientRow,
  isNewContributionRow,
  mergeDrafts,
  toContributionBody,
} from '~support-at-home/lib/contributionRows';
import {
  buildBulkUpdateRows,
  getPageSelectionState,
  groupSelectableByClient,
  pruneSelection,
  toggleAllOnPage,
  toggleIds,
} from '~support-at-home/lib/contributionSelection';

import { Button, ClickableIcon, ClickableUploadIcon, ErrorResponse, Toolbar } from '~components';
import ConfirmDialog from '~components/ConfirmDialog';
import Field from '~components/form-fields/Field';
import { SearchBar } from '~components/form-fields/search';
import Icon, { faFileImport, faMagnifyingGlass, faPenToSquare, faPlus } from '~components/icon';
import { Row, TableView } from '~components/table-view';
import { TableViewHandle } from '~components/table-view/TableView';
import AddModal from '~support-at-home/components/common/AddModal';
import BulkUpdateContributionsModal from '~support-at-home/components/config/contributions/BulkUpdateContributionsModal';
import ContributionFilters from '~support-at-home/components/config/contributions/ContributionFilters';

import columnsFactory from '~support-at-home/forms/client-contributions/list';
import searchFields from '~support-at-home/forms/client-contributions/search';

import { getAll as getContributionCategories } from '~lookups/actions/sahCategories';
import { getAll } from '~main/actions/clients';
import { uploadFile } from '~support-at-home/actions/clientContributions';
import { getAllSah } from '~support-at-home/actions/fundingTypes';
import {
  useCreateContributionMutation,
  useDeleteContributionMutation,
  useGetContributionsQuery,
  useUpdateContributionMutation,
} from '~support-at-home/actions/sahClientContributions';

import { clearError } from '~support-at-home/reducers/clientContributions';

import { selectSahCategories } from '~lookups/selectors/sahCategories';
import { selectLookupClients } from '~main/selectors/clients/clients';
import { selectUserTags } from '~main/selectors/login';
import { selectUploadError } from '~support-at-home/selectors/clientContributions';
import { selectSahFundingTypes } from '~support-at-home/selectors/fundingTypes';

const PAGE_SIZE = 30;

interface NewContribution {
  client_id: string;
  category_id: string;
  funding_source_id: number | null;
  contrib_proportion: number | null;
  start_date: string;
  end_date: string;
}

const emptyContribution: NewContribution = {
  client_id: '',
  category_id: '',
  funding_source_id: null,
  contrib_proportion: null,
  start_date: '',
  end_date: '',
};

const Contributions: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const dispatch = useAppDispatch();
  const categories = useAppSelector(selectSahCategories);
  const fundingSources = useAppSelector(selectSahFundingTypes);
  const uploadError = useAppSelector(selectUploadError);
  const userTags = useAppSelector(selectUserTags);
  // Every write (add, edit, upload, delete, bulk update) needs the same tag as the API's write routes.
  const canUpdate = userTags.includes(AuthTags.CLIENTS.BUDGETS.UPDATE);

  const tableRef = useRef<TableViewHandle>(null);
  // The row whose Save was pressed. Validation only looks at that row, so a legacy row that is
  // invalid elsewhere on the page cannot block every save.
  const savingRowIdRef = useRef<string | number | null>(null);
  // Rows in edit mode, so a refetch does not overwrite what is being typed. The ref is read straight
  // away (the merge below); the state copy re-renders the selection, which leaves these rows out.
  const editingIdsRef = useRef<Set<string | number>>(new Set());
  const [editingIds, setEditingIds] = useState<ReadonlySet<string | number>>(new Set());
  const setRowEditing = useCallback((rowId: string | number, isEditing: boolean) => {
    if (isEditing) editingIdsRef.current.add(rowId);
    else editingIdsRef.current.delete(rowId);
    setEditingIds(new Set(editingIdsRef.current));
  }, []);

  const [params, setParams] = useState({ limit: PAGE_SIZE });
  const [filters, setFilters] = useState<FilterValues>(EMPTY_FILTERS);
  const filterParams = useMemo(() => buildFilterParams(filters), [filters]);
  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearch('sah-client-contributions-search', {});
  // A new search starts again from the first page, as a changed filter does (the selection is pruned
  // by the effect below once the new rows load).
  const search = (values: { [key: string]: any }) => {
    setSearchParams(values);
    setParams({ limit: PAGE_SIZE });
  };

  const [data, setData] = useState<Row[]>([]);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string>('');

  // Ids of the contributions ticked for a bulk update.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkModalOpen, setBulkModalOpen] = useState(false);

  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [addError, setAddError] = useState<string>('');
  const [newContribution, setNewContribution] = useState<NewContribution>(emptyContribution);

  const {
    data: result,
    // isFetching, not isLoading: a filter, a search or LOAD MORE keeps the old rows on screen until the
    // new ones arrive, and only isFetching covers those refetches.
    isFetching,
    refetch,
  } = useGetContributionsQuery(
    { ...params, ...searchParams, ...filterParams },
    { skip: !isActive, refetchOnMountOrArgChange: true },
  );
  const [createContribution, { isLoading: isCreating }] = useCreateContributionMutation();
  const [updateContribution] = useUpdateContributionMutation();
  const [deleteContribution] = useDeleteContributionMutation();

  useEffect(() => {
    if (isActive) {
      dispatch(getAllSah());
      dispatch(getContributionCategories({ sort: ['sequence'] }));
    }
  }, [isActive, dispatch]);

  useEffect(() => {
    if (!result) return;
    setData((prev) => mergeDrafts(flattenContributionGroups(result.rows), prev, editingIdsRef.current));
  }, [result]);

  const today = dayjs().format(dateInputFieldFormat);

  // The selection survives LOAD MORE (the loaded rows only grow), but drops ids that have left the
  // loaded data (after a search, a delete, or a row that has since ended) and rows put into edit mode.
  useEffect(() => {
    setSelectedIds((prev) => pruneSelection(prev, data, today, editingIds));
  }, [data, today, editingIds]);

  // Client names by id, for the checkboxes' accessible labels (one lookup per checkbox, not a scan).
  const clientNames = useMemo(
    () => new Map<number, string>(data.filter(isClientRow).map((row) => [row.client_id, row.client_name])),
    [data],
  );

  const selectableIdsByClient = useMemo(
    () => groupSelectableByClient(data, today, editingIds),
    [data, today, editingIds],
  );
  const bulkRows = useMemo(() => buildBulkUpdateRows(data, selectedIds), [data, selectedIds]);

  const toggleContribution = useCallback((id: string) => setSelectedIds((prev) => toggleIds(prev, [id])), []);
  const toggleClient = useCallback(
    (clientId: number) => setSelectedIds((prev) => toggleIds(prev, selectableIdsByClient.get(clientId) ?? [])),
    [selectableIdsByClient],
  );
  const pageSelectionState = getPageSelectionState(selectableIdsByClient, selectedIds);
  const toggleAllLoaded = useCallback(
    () => setSelectedIds((prev) => toggleAllOnPage(prev, selectableIdsByClient)),
    [selectableIdsByClient],
  );
  const clearSelection = () => setSelectedIds(new Set());

  // A changed filter starts the list again from the first page.
  const changeFilters = useCallback((changes: Partial<FilterValues>) => {
    setFilters((prev) => ({ ...prev, ...changes }));
    setParams({ limit: PAGE_SIZE });
  }, []);
  const clearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setParams({ limit: PAGE_SIZE });
  }, []);

  const handleAddRowFor = useCallback((clientId: number) => {
    const newRow = buildNewContributionRow(clientId);
    setData((prev) => insertAfterClient(prev, clientId, newRow));
    setRowEditing(newRow.id, true);
    tableRef.current?.startEditing(newRow.id);
  }, [setRowEditing]);

  const handleEditRow = (row: Row, isEditing: boolean) => setRowEditing(row.id, isEditing);

  // Returns the promise so TableView keeps the row in edit mode, spinner showing, until the save
  // lands. A validation failure or a rejected request throws, which leaves the row in edit mode.
  const handleSaveRow = async (row: Row) => {
    savingRowIdRef.current = row.id;
    setActionError('');

    try {
      const isValid = await tableRef.current?.validate();
      if (!isValid) throw new Error('Contribution failed validation');

      const isNew = isNewContributionRow(row);
      const body = toContributionBody(row);

      try {
        if (isNew) {
          await createContribution(body).unwrap();
        } else {
          const { client_id: _clientId, ...updateBody } = body;
          await updateContribution({ id: String(row.id), body: updateBody }).unwrap();
        }
      } catch (error) {
        setActionError(getErrorMessage(error));
        throw error;
      }

      setRowEditing(row.id, false);
      // The saved row comes back from the server under its real id, so the local draft goes.
      if (isNew) setData((prev) => prev.filter((r) => r.id !== row.id));
    } finally {
      savingRowIdRef.current = null;
    }
  };

  const removeDraft = (rowId: string | number) => {
    setRowEditing(rowId, false);
    setData((prev) => prev.filter((r) => r.id !== rowId));
  };

  const handleDeleteRow = (rowId: string | number) => {
    const row = data.find((r) => r.id === rowId);
    if (!row) return;

    // An unsaved row has nothing on the server to delete.
    if (isNewContributionRow(row)) {
      removeDraft(rowId);
      return;
    }

    setPendingDeleteId(String(rowId));
  };

  const handleDeleteConfirm = async (confirmed: boolean) => {
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    if (!confirmed || !id) return;

    setActionError('');
    setDeletingIds((prev) => new Set(prev).add(id));
    try {
      await deleteContribution(id).unwrap();
    } catch (error) {
      setActionError(getErrorMessage(error));
      // e.g. already deleted elsewhere: re-read so the list matches the server.
      refetch();
    } finally {
      setRowEditing(id, false);
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const isDeleting = (row: Row) => deletingIds.has(String(row.id));

  const onUpload = async (file: File, setProgress: (value: number) => void) => {
    try {
      dispatch(clearError([]));
      await dispatch(uploadFile({ file, setProgress })).unwrap();
      await refetch();
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  // The toolbar's Add Contribution is still how a client with no contributions yet gets its first.
  const isAddValid =
    !!newContribution.client_id &&
    !!newContribution.category_id &&
    newContribution.funding_source_id != null &&
    Number.isFinite(newContribution.funding_source_id) &&
    newContribution.contrib_proportion !== null &&
    !isNaN(newContribution.contrib_proportion) &&
    newContribution.contrib_proportion >= 0 &&
    !!newContribution.start_date &&
    (!newContribution.end_date || newContribution.end_date >= newContribution.start_date);

  const openAddModal = () => {
    setNewContribution(emptyContribution);
    setAddError('');
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    if (isCreating) return;
    setAddModalOpen(false);
  };

  const handleAddContributionChange = (name: string, value: any) => {
    setNewContribution((prev) => ({ ...prev, [name as keyof NewContribution]: value }));
  };

  const handleCategoryChange = (name: string, value: any) => {
    setNewContribution((prev) => ({ ...prev, [name]: value, contrib_proportion: null }));
  };

  const handleContributionProportionChange = (name: string, value: any) => {
    if (value === '' || value === null || value === undefined) {
      value = null;
    } else {
      const numValue = parseFloat(value);
      if (
        !isNaN(numValue) &&
        numValue >= 0 &&
        numValue <= Number(categories.find((x) => x.id === newContribution.category_id)?.max_contrib_proportion)
      ) {
        value = numValue;
      } else return;
    }

    handleAddContributionChange(name, value);
  };

  const handleAddContributionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAddValid || isCreating) return;

    setAddError('');
    try {
      await createContribution({
        client_id: Number(newContribution.client_id),
        category_id: newContribution.category_id,
        funding_source_id: newContribution.funding_source_id as number,
        contrib_proportion: newContribution.contrib_proportion as number,
        start_date: newContribution.start_date,
        end_date: newContribution.end_date || null,
      }).unwrap();
      setAddModalOpen(false);
    } catch (error) {
      setAddError(getErrorMessage(error));
    }
  };

  // Not memoised: the columns close over live state (today, the handlers), so a memo would go stale.
  const columns = columnsFactory({
    categories,
    fundingSources,
    today,
    canAddForClient: (row) => canUpdate && row.has_client === true,
    onAddContribution: handleAddRowFor,
    // No checkboxes without the tag: the bulk update would only be refused.
    ...(canUpdate && {
      headerSelection: {
        state: pageSelectionState,
        disabled: selectableIdsByClient.size === 0,
        onToggle: toggleAllLoaded,
      },
      selection: {
        selectedIds,
        selectableIdsByClient,
        onToggleContribution: toggleContribution,
        onToggleClient: toggleClient,
        clientName: (clientId: number) => clientNames.get(clientId) ?? `Unknown client (#${clientId})`,
      },
    }),
  });

  const selectedMaxProportion = useMemo(
    () => categories.find((x) => x.id === newContribution.category_id)?.max_contrib_proportion,
    [categories, newContribution.category_id],
  );

  return (
    <div className="p-2">
      <ErrorResponse message={actionError || uploadError || ''} dataCy="sah-client-contributions-error" />
      <Toolbar
        id="contributions"
        leftCluster={
          <ContributionFilters
            filters={filters}
            categories={categories}
            fundingSources={fundingSources}
            onChange={changeFilters}
          />
        }
        rightCluster={
          <>
            <ClickableUploadIcon
              id="sah-client-contributions-file-upload-button"
              icon={faFileImport}
              toolTip="Import CSV / JSON file"
              onUpload={onUpload}
              externalError={uploadError}
              authTag={AuthTags.CLIENTS.BUDGETS.UPDATE}
            />
            <ClickableIcon
              id="sah-client-contributions-search-toggle"
              icon={faMagnifyingGlass}
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
              ariaLabel="Toggle Search Bar"
            />
            {/* One action slot at a fixed width: Add Contribution normally, Bulk Update while rows are
                selected. Swapping them in the same slot keeps the toolbar, and so the filters, the same
                width either way. View-only users get no action button. */}
            {canUpdate &&
              (selectedIds.size > 0 ? (
                <Button
                  id="sah-client-contributions-bulk-update-button"
                  className="sah-toolbar-action-button"
                  size="sm"
                  color="success"
                  onClick={() => setBulkModalOpen(true)}
                >
                  <Icon className="me-2" icon={faPenToSquare} />
                  {`Bulk Update (${selectedIds.size})`}
                </Button>
              ) : (
                <Button
                  id="sah-client-contributions-add-button"
                  className="sah-toolbar-action-button"
                  size="sm"
                  color="success"
                  onClick={openAddModal}
                >
                  <Icon className="me-2" icon={faPlus} />
                  Add Contribution
                </Button>
              ))}
          </>
        }
      />
      <SearchBar
        isOpen={searchBarOpen}
        fields={searchFields}
        onSubmit={search}
        submitLabel="Search"
        showSubmitIcon={false}
        stretchFields
      />
      {/* A fixed-height strip, so the table never jumps when the selection changes. */}
      <div className="sah-selection-strip" data-cy="sah-client-contributions-selection-strip">
        {/* Always rendered (empty when nothing is selected) so screen readers hear the count change. */}
        <span role="status" aria-live="polite">
          {selectedIds.size > 0 &&
            `${selectedIds.size} ${selectedIds.size === 1 ? 'contribution' : 'contributions'} selected`}
        </span>
        {selectedIds.size > 0 && (
          <Button
            id="sah-client-contributions-clear-selection-button"
            size="sm"
            color="link"
            className="p-0"
            onClick={clearSelection}
          >
            Clear selection
          </Button>
        )}
        {selectedIds.size > 0 && hasActiveFilters(filters) && (
          <span className="sah-selection-strip-separator text-muted" aria-hidden="true">
            |
          </span>
        )}
        {hasActiveFilters(filters) && (
          <Button
            id="sah-client-contributions-clear-filters-button"
            size="sm"
            color="link"
            className="p-0"
            onClick={clearFilters}
          >
            Clear filters
          </Button>
        )}
      </div>
      {/* Holds the table to the width of the tab and gives it its own scrollbars, so a narrow window
          scrolls the table rather than the whole page (see .sah-contributions-scroll). */}
      <div className="sah-contributions-scroll" aria-busy={isFetching}>
        <TableView
          ref={tableRef}
          id="sah_client_contributions_table"
          columns={columns}
          data={data}
          isLoading={isFetching}
          emptyMessage="No client SaH Contributions found"
          showAddRow={false}
          allowPagination
          totalCount={result?.total ?? 0}
          loadedCount={result?.count ?? 0}
          // Ignored while a page is still loading, so a double click asks for one more page, not two.
          onLoadMore={() => {
            if (!isFetching) setParams((prev) => ({ limit: prev.limit + PAGE_SIZE }));
          }}
          onChange={setData}
          onEditRow={handleEditRow}
          onSaveRow={handleSaveRow}
          onDeleteRow={handleDeleteRow}
          canEditRow={(row) => canUpdate && !isClientRow(row) && !isDeleting(row)}
          canDeleteRow={(row) => canUpdate && !isClientRow(row) && !isDeleting(row)}
          isRowDisabled={isDeleting}
          canRowValidate={(row) => row.id === savingRowIdRef.current}
          rowClassName={(row) => (isClientRow(row) ? 'client-row' : 'contribution-row')}
        />
      </div>

      {isBulkModalOpen && (
        <BulkUpdateContributionsModal
          rows={bulkRows}
          today={today}
          onClose={() => setBulkModalOpen(false)}
          onSuccess={() => {
            setBulkModalOpen(false);
            clearSelection();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={pendingDeleteId !== null}
        useList={false}
        messages={['This contribution will be deleted.']}
        confirmColor="danger"
        onClose={handleDeleteConfirm}
      />

      <AddModal
        isOpen={isAddModalOpen}
        title="Add New Contribution"
        onClose={closeAddModal}
        onSubmit={handleAddContributionSubmit}
        submitLabel="Add Contribution"
        submitDisabled={!isAddValid}
        isSubmitting={isCreating}
      >
        <ErrorResponse message={addError} dataCy="sah-client-contributions-add-error" />
        <Field
          id="client_id"
          caption="Client"
          type="select-lazy"
          value={newContribution.client_id}
          selectorOptions={selectLookupClients}
          selectorActions={getAll}
          required
          fieldId="client_id"
          handleChange={handleAddContributionChange}
        />
        <Field
          id="category_id"
          caption="Category"
          type="select"
          value={newContribution.category_id}
          options={categories.map((c: any) => ({ id: c.id, name: c.name }))}
          handleChange={handleCategoryChange}
          fieldId="category_id"
          required
        />
        <Field
          id="funding_source_id"
          caption="Funding Source"
          type="select"
          value={newContribution.funding_source_id}
          selectorActions={getAllSah}
          options={fundingSources.map((f: any) => ({ id: f.id, name: f.name }))}
          handleChange={handleAddContributionChange}
          fieldId="funding_source_id"
          required
        />
        <Field
          id="contrib_proportion"
          caption="Contribution"
          type="number"
          value={newContribution.contrib_proportion}
          handleChange={handleContributionProportionChange}
          fieldId="contrib_proportion"
          disabled={!newContribution.category_id}
          warning={newContribution.category_id ? `Maximum contribution proportion ${selectedMaxProportion}%` : ''}
          required
        />
        <Field
          id="start_date"
          caption="Start Date"
          type="date"
          value={newContribution.start_date}
          handleChange={handleAddContributionChange}
          fieldId="start_date"
          required
        />
        <Field
          id="end_date"
          caption="End Date"
          type="date"
          value={newContribution.end_date}
          handleChange={handleAddContributionChange}
          fieldId="end_date"
        />
      </AddModal>
    </div>
  );
};

export default Contributions;
